import { produce } from 'immer';
import { find } from 'lodash-es';
import { createSelector } from 'reselect';
import { asyncAction } from './async-action';
import { ScopedAsyncType, ScopedType } from './scoped-type';
import * as api from '../../lib/api';

const initialState = {
  fields: [],
  loading: false,
  contractedGrowerFields: [],
  additionalServices: [],
};

const SLICE = 'additional-services';
const asyncType = ScopedAsyncType(SLICE);
const scopeType = ScopedType(SLICE);

const LOAD_GROWER_FIELDS = asyncType('LOAD_GROWER_FIELDS');
const TOGGLE_ADDITIONAL_SERVICE_ENROLLMENT = asyncType(
  'TOGGLE_ADDITIONAL_SERVICE_ENROLLMENT',
);
const ADDITIONAL_SERVICES = scopeType('ADDITIONAL_SERVICES');
const BASE_ADDITIONAL_SERVICES = ['VPI'];

export const selectors = {
  loading: (state) => state[SLICE].loading,
  fields: (state) => state[SLICE].fields,
  contractedGrowerFields: createSelector(
    (state) => state[SLICE].fields,
    (state) => state.fields.growerFields,
    (moduleFields, growerFields) =>
      moduleFields.filter((field) =>
        find(growerFields, {
          aaManagedId: field.aaManagedId,
          isContractedV2: true,
        }),
      ),
  ),
  additionalServices: (state) => state[SLICE].additionalServices,
};

export const actions = {
  loadFields,
  toggleAdditionalServiceEnrollment,
  loadAdditionalServices,
};

function loadFields(growerId) {
  return asyncAction(LOAD_GROWER_FIELDS, async (dispatch) => {
    dispatch(loadAdditionalServices());

    return api.getAdditionalServices(growerId);
  });
}

function toggleAdditionalServiceEnrollment(
  growerId,
  aaids,
  additionalService,
  status,
) {
  return asyncAction(
    TOGGLE_ADDITIONAL_SERVICE_ENROLLMENT,
    () =>
      api.updateAdditionalServices(growerId, aaids, additionalService, status),
    {
      startPayload: { aaids, additionalService, status },
      failurePayload: { aaids, additionalService, status },
    },
  );
}

function loadAdditionalServices() {
  return (dispatch) => {
    dispatch({ type: ADDITIONAL_SERVICES, payload: BASE_ADDITIONAL_SERVICES });
  };
}

const reducerHandlers = {
  [LOAD_GROWER_FIELDS.start]: (state) => {
    state.fields = [];
    state.loading = true;
  },
  [LOAD_GROWER_FIELDS.complete]: (state, payload) => {
    state.fields = payload;
    state.loading = false;
  },
  [LOAD_GROWER_FIELDS.failure]: (state) => {
    state.fields = [];
    state.loading = false;
  },
  [TOGGLE_ADDITIONAL_SERVICE_ENROLLMENT.start]: (state, payload) => {
    const additionalService = payload.additionalService;

    state.fields.forEach((field) => {
      if (payload.aaids.includes(field.aaManagedId)) {
        if (
          state.additionalServices.includes(additionalService) &&
          payload.status === 'ACTIVE'
        ) {
          field.additionalServices.push(additionalService);
        }
        if (
          state.additionalServices.includes(additionalService) &&
          payload.status === 'INACTIVE'
        ) {
          field.additionalServices = field.additionalServices.filter(
            (service) => service !== additionalService,
          );
        }
      }
    });
  },
  [TOGGLE_ADDITIONAL_SERVICE_ENROLLMENT.failure]: (state, payload) => {
    const additionalService = payload.additionalService;

    state.fields.forEach((field) => {
      if (payload.aaids.includes(field.aaManagedId)) {
        if (
          state.additionalServices.includes(additionalService) &&
          payload.status === 'ACTIVE'
        ) {
          field.additionalServices = field.additionalServices.filter(
            (service) => service !== additionalService,
          );
        }
        if (
          state.additionalServices.includes(additionalService) &&
          payload.status === 'INACTIVE'
        ) {
          field.additionalServices.push(additionalService);
        }
      }
    });
  },
  [ADDITIONAL_SERVICES]: (state, payload) => {
    state.additionalServices = payload;
  },
};

export const reducers = {
  [SLICE]: (state = initialState, { type, payload } = {}) => {
    if (!reducerHandlers[type]) return state;
    return produce(state, (draft) => reducerHandlers[type](draft, payload));
  },
};
